import React, { useState, useEffect, useMemo, useCallback } from "react";
import SocialActions from '../../components/SocialActions/SocialActions';
import ContentEditorModal from '../../components/ContentEditorModal/ContentEditorModal';
import { useAuth } from '../../contexts/AuthContext';

const Filseta = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('both');

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isNewContent, setIsNewContent] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const apiURL = process.env.REACT_APP_API_URL || '/api';

  const loadTexts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiURL}/content?type=filseta`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const mapped = json.data.map((item, idx) => {
            const content = item.content || {};
            return {
              _id: item._id,
              itemId: item.itemId || idx + 1,
              title_amharic: content.title_amharic || item.title || '',
              title_geez: content.title_geez || item.titleGeez || '',
              stanzas: Array.isArray(content.stanzas) ? content.stanzas : (item.stanzas || []),
              raw: item
            };
          });
          setTexts(mapped);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('API error for Filseta:', err.message);
    }
    setTexts([]);
    setLoading(false);
  }, [apiURL]);

  useEffect(() => {
    loadTexts();
  }, [loadTexts]);

  const filteredTexts = useMemo(() => {
    const searchLower = search.toLowerCase();

    return texts
      .map((item) => {
        // Filter each stanza individually
        const filteredStanzas = item.stanzas?.filter((stanza) => {
          return (
            stanza.stanza_number_geez?.toString().includes(searchLower) ||
            stanza.geez?.toLowerCase().includes(searchLower) ||
            stanza.amharic?.toLowerCase().includes(searchLower)
          );
        });

        // Include the item only if it has matching stanzas or title
        if (
          (item.title_amharic && item.title_amharic.toLowerCase().includes(searchLower)) ||
          (item.title_geez && item.title_geez.toLowerCase().includes(searchLower)) ||
          (filteredStanzas && filteredStanzas.length > 0)
        ) {
          return { ...item, stanzas: filteredStanzas || [] };
        }

        return null;
      })
      .filter(Boolean);
  }, [search, texts]);

  const highlightText = (text) => {
    if (!search.trim() || !text) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  };

  const handleOpenEdit = (item) => {
    setEditingItem({
      _id: item._id,
      contentType: 'filseta',
      title: item.title_amharic || item.title_geez,
      itemId: item.itemId,
      content: {
        title_amharic: item.title_amharic,
        title_geez: item.title_geez,
        stanzas: item.stanzas
      }
    });
    setIsNewContent(false);
    setEditorOpen(true);
  };

  const handleOpenInsert = () => {
    const nextId = texts.length > 0 ? Math.max(...texts.map(t => typeof t.itemId === 'number' ? t.itemId : 0)) + 1 : 1;
    setEditingItem({
      contentType: 'filseta',
      title: 'ፍልሰታ ክፍል ' + nextId,
      itemId: nextId,
      content: {
        title_amharic: '',
        title_geez: '',
        stanzas: [
          {
            stanza_number_geez: '፩',
            geez: '',
            amharic: ''
          }
        ]
      }
    });
    setIsNewContent(true);
    setEditorOpen(true);
  };

  const canEditContent = user?.canEdit || user?.canUpdate || user?.isAdmin;
  const canInsertContent = user?.canInsert || user?.isAdmin;

  const handleEditorSaved = () => {
    loadTexts();
  };

  return (
    <div className="page-container filseta-theme">
      {/* Enhanced Page Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="page-main-title">
              🎵 እትአመነኪ ማርያም
            </h1>
            <div className="page-subtitle">
              ወመልክዐ ፍልሰታ - Marian Hymns and Filseta (ከዳታቤዝ)
            </div>
            <div className="page-description">
              <p className="page-description-main">
                የእመቤታችን ቅድስት ድንግል ማርያም መዝሙሮች እና የፍልሰታ መልክዓ ግዕዝ
              </p>
              <p className="page-description-sub">
                Sacred hymns and praises dedicated to Our Lady, the Holy Virgin Mary
              </p>
            </div>
          </div>
          {canInsertContent && (
            <button
              onClick={handleOpenInsert}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-2 self-start sm:self-auto cursor-pointer"
            >
              <span>➕</span>
              <span>አዲስ መዝሙር አክል</span>
            </button>
          )}
        </div>
      </div>

      {/* Controls Section */}
      <div className="controls-container">
        <div className="controls-grid">
          <div className="control-group">
            <label htmlFor="filseta-search" className="control-label">
              🔍 ፈልግ (Search)
            </label>
            <input
              id="filseta-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ፈልግ በስም፣ ቁጥር፣ ወይም ጽሑፍ..."
              className="control-input"
            />
          </div>

          <div className="control-group">
            <label className="control-label">🌐 ቋንቋ (Language):</label>
            <div className="language-buttons flex gap-2">
              <button
                onClick={() => setSelectedLanguage('amharic')}
                className={`language-btn px-3 py-2 rounded-lg border transition-all duration-200 text-sm font-medium ${selectedLanguage === 'amharic' ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent shadow-md' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
              >
                አማርኛ
              </button>
              <button
                onClick={() => setSelectedLanguage('geez')}
                className={`language-btn px-3 py-2 rounded-lg border transition-all duration-200 text-sm font-medium ${selectedLanguage === 'geez' ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent shadow-md' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
              >
                ግዕዝ
              </button>
              <button
                onClick={() => setSelectedLanguage('both')}
                className={`language-btn px-3 py-2 rounded-lg border transition-all duration-200 text-sm font-medium ${selectedLanguage === 'both' ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent shadow-md' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
              >
                ሁለቱም
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="content-container">
        <div className="content-header">
          <div className="content-icon">🎵</div>
          <h2 className="content-title">
            የፍልሰታ መዝሙሮች ({filteredTexts.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">በመጫን ላይ...</div>
        ) : filteredTexts.length > 0 ? (
          <div className="card-grid">
            {filteredTexts.map((item, index) => (
              <div key={item._id || index} className="card-item">
                <div className="card-header flex justify-between items-start">
                  <div>
                    <h3 className="card-title">
                      <span className="card-number">{index + 1}</span>
                      <span 
                        dangerouslySetInnerHTML={{
                          __html: highlightText(item.title_amharic || '')
                        }}
                      />
                    </h3>
                    <p className="card-subtitle" 
                       dangerouslySetInnerHTML={{
                         __html: highlightText(item.title_geez || '')
                       }}
                    />
                  </div>
                  {canEditContent && (
                    <button
                      onClick={() => handleOpenEdit(item)}
                      className="bg-amber-500 hover:bg-amber-600 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm transition-all cursor-pointer flex-shrink-0"
                    >
                      <span>✏️</span>
                      <span>አርትዕ</span>
                    </button>
                  )}
                </div>

                {item.stanzas?.length > 0 ? (
                  <div className="card-content">
                    <div className="stanzas-container space-y-4">
                      {item.stanzas.map((stanza, i) => (
                        <div key={i} className="stanza-item">
                          {(selectedLanguage === 'geez' || selectedLanguage === 'both') && (
                            <div className="content-block geez-content">
                              <div className="content-header">
                                <span className="language-indicator geez">
                                  ግዕዝ፡ {stanza.stanza_number_geez}
                                </span>
                              </div>
                              <div className="content-text">
                                <p 
                                  dangerouslySetInnerHTML={{
                                    __html: highlightText(stanza.geez || '')
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          
                          {(selectedLanguage === 'amharic' || selectedLanguage === 'both') && (
                            <div className="content-block amharic-content">
                              <div className="content-header">
                                <span className="language-indicator amharic">
                                  አማርኛ
                                </span>
                              </div>
                              <div className="content-text">
                                <p 
                                  dangerouslySetInnerHTML={{
                                    __html: highlightText(stanza.amharic || '')
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Social Actions */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <SocialActions
                        contentId={`filseta_${item._id || index}`}
                        contentType="filseta"
                        title={item.title_amharic || item.title_geez}
                        content={item.stanzas?.map(s => s.amharic || s.geez).join(' ').substring(0, 300)}
                        showBookmark={true}
                        showShare={true}
                        showLike={true}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="no-results">
                    <div className="no-results-icon">🔍</div>
                    <p className="no-results-title">ምንም ተዛማጅ ጥቅሶች አልተገኙም</p>
                    <p className="no-results-subtitle">No matching stanzas found</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <div className="no-results-icon">🎵</div>
            <p className="no-results-title">ምንም ውጤት አልተገኘም</p>
            <p className="no-results-subtitle">No results found - የለም</p>
          </div>
        )}
      </div>

      {/* Instructions Section */}
      <div className="content-container">
        <div className="content-header">
          <div className="content-icon">📖</div>
          <h2 className="content-title">
            የፍልሰታ መዝሙር መመሪያ
          </h2>
        </div>
        
        <div className="instructions-grid">
          <div className="instruction-card">
            <div className="instruction-header">
              <span className="instruction-icon">🔍</span>
              <h3 className="instruction-title">እንዴት እንደምንጠቀም</h3>
            </div>
            <p className="instruction-description">
              ከላይ ባለው ፍለጋ ሳጥን ውስጥ የሚፈልጉትን ቃል ወይም ሐረግ ያስገቡ። በአማርኛ ወይም በግዕዝ መፈለግ ይችላሉ።
            </p>
          </div>

          <div className="instruction-card">
            <div className="instruction-header">
              <span className="instruction-icon">🌐</span>
              <h3 className="instruction-title">ቋንቋ መምረጥ</h3>
            </div>
            <p className="instruction-description">
              የሚፈልጉትን ቋንቋ ይምረጡ፦ አማርኛ፣ ግዕዝ ወይም ሁለቱንም በአንድ ላይ ማየት ይችላሉ።
            </p>
          </div>

          <div className="instruction-card">
            <div className="instruction-header">
              <span className="instruction-icon">🎵</span>
              <h3 className="instruction-title">መዝሙሮችን ማንበብ</h3>
            </div>
            <p className="instruction-description">
              እያንዳንዱ መዝሙር በቁጥር የተደራጀ ሲሆን በግዕዝና በአማርኛ ቋንቋ ተተርጉሟል።
            </p>
          </div>

          <div className="instruction-card">
            <div className="instruction-header">
              <span className="instruction-icon">⭐</span>
              <h3 className="instruction-title">ልዩ ማስታወሻ</h3>
            </div>
            <p className="instruction-description">
              የፍልሰታ መዝሙሮች የእመቤታችን ቅድስት ድንግል ማርያም ውዳሴዎች ናቸው። በአክብሮት ያንብቡ።
            </p>
          </div>
        </div>
      </div>

      {/* Editor Modal */}
      {editorOpen && (
        <ContentEditorModal
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          onSaved={handleEditorSaved}
          initialData={editingItem}
          isNew={isNewContent}
          contentType="filseta"
        />
      )}
    </div>
  );
};

export default Filseta;
